import { useState, useCallback, useRef } from 'react';
import API, { getSchemaName } from 'shared/api';

const LIMITE_POR_PAGINA = 500;

const useTrabajadorData = () => {
  const [conversacionesBot, setConversacionesBot] = useState([]);
  const [conversacionesAsesor, setConversacionesAsesor] = useState([]);
  const [clientesPendientes, setClientesPendientes] = useState([]);
  const [mensajes, setMensajes] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [ultimoId, setUltimoId] = useState(null);
  const ultimoIdRef = useRef(null);
  const currentClienteIdRef = useRef(null);
  const currentPedidoIdRef = useRef(null);
  const mensajesRef = useRef([]);

  const cargarConversacionesBot = useCallback(async (codigoSede = '') => {
    try {
      const params = new URLSearchParams();
      params.append('tipo', 'bot');
      if (codigoSede) params.append('codigo_sede', codigoSede);
      const res = await API.get(`/pedidos/con-versaciones?${params.toString()}`);
      if (res.data.status === 'success') setConversacionesBot(res.data.data);
    } catch (e) {
      console.error('Error cargando conversaciones bot:', e);
    }
  }, []);

  const cargarConversacionesAsesor = useCallback(async (codigoSede = '') => {
    try {
      const params = new URLSearchParams();
      params.append('tipo', 'asesor');
      if (codigoSede) params.append('codigo_sede', codigoSede);
      const res = await API.get(`/pedidos/con-versaciones?${params.toString()}`);
      if (res.data.status === 'success') setConversacionesAsesor(res.data.data);
    } catch (e) {
      console.error('Error cargando conversaciones asesor:', e);
    }
  }, []);

  const cargarClientesPendientes = useCallback(async (codigoSede = '') => {
    try {
      const params = new URLSearchParams();
      if (codigoSede) params.append('codigo_sede', codigoSede);
      const res = await API.get(
        `/chats/clientes-pendientes/${getSchemaName()}?${params.toString()}`
      );
      if (res.data.status === 'success') setClientesPendientes(res.data.data);
    } catch (e) {
      console.error('Error cargando clientes pendientes:', e);
    }
  }, []);

  const cargarMensajesDesdeAPI = useCallback(async (clienteId, pedidoId, ultimoIdParam = null) => {
    const params = new URLSearchParams();
    params.append('limite', LIMITE_POR_PAGINA);
    if (pedidoId) params.append('pedido_id', pedidoId);
    if (ultimoIdParam) params.append('ultimo_id', ultimoIdParam);
    const res = await API.get(`/chats/por-cliente-paginado/${clienteId}?${params.toString()}`);
    if (res.data.status === 'success')
      return { mensajes: res.data.data, pagination: res.data.pagination };
    return { mensajes: [], pagination: { total: 0, has_more: false } };
  }, []);

  const cargarMensajes = useCallback(
    async (clienteId, pedidoId, reset = true) => {
      if (!clienteId) return;
      currentClienteIdRef.current = clienteId;
      currentPedidoIdRef.current = pedidoId;
      const result = await cargarMensajesDesdeAPI(
        clienteId,
        pedidoId,
        reset ? null : ultimoIdRef.current
      );
      if (reset) {
        setMensajes(result.mensajes);
      } else {
        setMensajes((prev) => [...result.mensajes, ...prev]);
      }
      setHasMore(result.pagination.has_more);
      const nuevoUltimoId = result.pagination.ultimo_id;
      setUltimoId(nuevoUltimoId);
      ultimoIdRef.current = nuevoUltimoId;
      mensajesRef.current = result.mensajes;
    },
    [cargarMensajesDesdeAPI]
  );

  const cargarMasMensajes = useCallback(async () => {
    const clienteId = currentClienteIdRef.current;
    const pedidoId = currentPedidoIdRef.current;
    if (!hasMore || cargandoMas || !clienteId) return;
    setCargandoMas(true);
    await cargarMensajes(clienteId, pedidoId, false);
    setCargandoMas(false);
  }, [hasMore, cargandoMas, cargarMensajes]);

  const recargarMensajes = useCallback(
    async (clienteId, pedidoId) => {
      if (!clienteId) return;
      const result = await cargarMensajesDesdeAPI(clienteId, pedidoId, null);
      setMensajes((prev) => {
        const nuevos = result.mensajes || [];
        if (nuevos.length === 0) return prev;
        const existingIds = new Set(prev.map((m) => m.id));
        const added = nuevos.filter((m) => !existingIds.has(m.id));
        if (added.length === 0) return prev;
        return [...prev, ...added].sort((a, b) => (a.id === b.id ? 0 : a.id < b.id ? -1 : 1));
      });
      setHasMore(result.pagination.has_more);
      const nuevoUltimoId = result.pagination.ultimo_id;
      setUltimoId(nuevoUltimoId);
      ultimoIdRef.current = nuevoUltimoId;
    },
    [cargarMensajesDesdeAPI]
  );

  const marcarMensajesComoLeidos = useCallback(async (clienteId, pedidoId) => {
    if (!clienteId) return;
    try {
      let url = `/chats/marcar-leidos/${clienteId}`;
      if (pedidoId) url += `?pedido_id=${pedidoId}`;
      await API.put(url);
      setConversacionesBot((prev) =>
        prev.map((c) => (parseInt(c.id) === parseInt(pedidoId) ? { ...c, noLeidos: 0 } : c))
      );
      setConversacionesAsesor((prev) =>
        prev.map((c) => (parseInt(c.id) === parseInt(pedidoId) ? { ...c, noLeidos: 0 } : c))
      );
    } catch (e) {
      console.error('Error marcando leidos:', e);
    }
  }, []);

  const enviarMensaje = useCallback(async (idcliente, mensaje, pedidoId = null, respuestaAId = null) => {
    try {
      const res = await API.post('/chats/agregar', {
        mensaje,
        tipo: 'texto',
        autor: 'asesor',
        idcliente,
        pedido_id: pedidoId,
        respuesta_a_id: respuestaAId,
      });
      return { success: res.data.status === 'success', data: res.data.data };
    } catch (e) {
      console.error('Error enviando mensaje:', e);
      return { success: false, error: e };
    }
  }, []);

  const tomarChat = useCallback(async (clienteId) => {
    try {
      const res = await API.put(`/clientes/${clienteId}/tomar`);
      return res.data.status === 'success';
    } catch (e) {
      console.error('Error tomando chat:', e);
      return false;
    }
  }, []);

  const liberarChat = useCallback(async (clienteId) => {
    try {
      const res = await API.put(`/clientes/${clienteId}/liberar`);
      return res.data.status === 'success';
    } catch (e) {
      console.error('Error liberando chat:', e);
      return false;
    }
  }, []);

  const cargarPedidos = useCallback(async (codigoSede = '') => {
    try {
      const params = new URLSearchParams();
      params.append('limite', '50');
      if (codigoSede) params.append('codigo_sede', codigoSede);
      let url = `/pedidos/listar/${getSchemaName()}?${params.toString()}`;
      const res = await API.get(url);
      if (res.data.status === 'success') setPedidos(res.data.data);
    } catch (e) {
      console.error('Error cargando pedidos:', e);
    }
  }, []);

  const cargarSedes = useCallback(async () => {
    try {
      const res = await API.get('/usuarios/sedes');
      if (res.data.status === 'success') setSedes(res.data.data.filter((s) => s.activa !== false));
    } catch (e) {
      console.error('Error cargando sedes:', e);
    }
  }, []);

  const inicializar = useCallback(
    async (sedeFilter, isAdmin) => {
      setCargando(true);
      await Promise.all([
        cargarConversacionesBot(sedeFilter),
        cargarConversacionesAsesor(sedeFilter),
        cargarClientesPendientes(sedeFilter),
        cargarPedidos(sedeFilter),
        isAdmin ? cargarSedes() : Promise.resolve(),
      ]);
      setCargando(false);
    },
    [
      cargarConversacionesBot,
      cargarConversacionesAsesor,
      cargarClientesPendientes,
      cargarPedidos,
      cargarSedes,
    ]
  );

  return {
    clientesPendientes,
    conversacionesBot,
    conversacionesAsesor,
    mensajes,
    pedidos,
    sedes,
    cargando,
    cargandoMas,
    hasMore,
    ultimoId,
    cargarConversacionesBot,
    cargarConversacionesAsesor,
    cargarClientesPendientes,
    cargarMensajes,
    cargarMasMensajes,
    recargarMensajes,
    marcarMensajesComoLeidos,
    enviarMensaje,
    tomarChat,
    liberarChat,
    cargarPedidos,
    cargarSedes,
    inicializar,
  };
};

export default useTrabajadorData;
